import { FC, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { toastLocalizedOperationCatcher } from "../../../utils/alerts";
import { useAv } from "../../../utils/av";
import {
  DEFAULT_BREAKOUT_ADD_MORE_TIME_DURATION,
  MAX_BREAKOUT_DURATION,
  MIN_BREAKOUT_DURATION,
} from "../../../utils/breakouts";
import { getCurrentServerTimeMs, useTimeRemaining } from "../../../utils/hooks/use-time-remaining";
import { Link } from "../../../utils/routing";
import { useOverlayControls } from "../../../utils/routing/hooks/use-overlay-controls";
import { pageUrl } from "../../../utils/urls";
import { PROFILE_PROPERTIES, getUserFullName } from "../../../utils/user-utils";

import {
  serverEndBreakouts,
  serverExtendBreakoutTime,
  serverUpdateClassUserBreakoutGroup,
} from "api/api-server-actions";
import { SecondaryOverlay } from "components/constants";
import { NumberFieldWithStepperButtons } from "components/forms/NumberFieldWithStepperButtons";
import { BreakoutListeningToggleButton } from "components/materials/sidebar/BreakoutListeningToggleButton";
import { SidebarOptionsMenu } from "components/menus/SidebarOptionsMenu";
import { ClassUserType } from "components/server-types";
import { Trans, t } from "i18n/i18n";
import { AccordionList } from "mds/components/AccordionList";
import { Button } from "mds/components/Button";
import { CloseButton } from "mds/components/CloseButton";
import { DragAndDrop } from "mds/components/DragAndDrop";
import { IconText } from "mds/components/IconText";
import { LinkButton } from "mds/components/LinkButton";
import { ListItem } from "mds/components/ListItem";
import { MenuItem, SubMenu } from "mds/components/Menu";
import { Tooltip } from "mds/components/Tooltip";
import {
  CircleInfoIcon,
  ClockIcon,
  CopySendArrowIcon,
  PersonGroupIcon,
  PersonSingleIcon,
  SoundLevelAnimatedIcon,
  StarIcon,
} from "mds/icons";
import { useCapabilities, useUserIdsWhoCanAuthorSection } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  pageSelectors,
  selectActiveClassSession,
  selectActiveClassSessionId,
  selectClassUsersInMainCall,
  selectCurrentClassUser,
  selectCurrentClassUserBreakoutGroupId,
  selectCurrentClassUserId,
  selectCurrentSectionId,
  selectUserByClassUserId,
} from "store/selectors";
import {
  selectFullBreakoutGroups,
  selectIsInBreakout,
  selectOrderedBreakoutGroups,
} from "store/selectors/breakouts";
import { selectIsVirtualUserSpeaking } from "store/selectors/pubsub-virtual-class-state";
import { useListSelector, useObjectSelector } from "store/store-hooks";

interface PersonCountProps {
  count: number;
}
const PersonCount: FC<PersonCountProps> = ({ count }) => {
  return (
    <div className="flex items-center gap-1 text-sm font-medium text-black-tint-20">
      {count}
      <PersonSingleIcon size="xs" />
    </div>
  );
};

interface BreakoutRoomControlsProps {
  count: number;
  breakoutGroupId: string;
}
const BreakoutRoomControls: FC<BreakoutRoomControlsProps> = ({ count, breakoutGroupId }) => {
  const currentClassUser = useAppSelector(selectCurrentClassUser);
  // We need to know if the user is in any breakout room to show/hide the Listen button
  const isInABreakoutRoom = !!currentClassUser?.breakout_group_id;
  // We need to know if the user is in this specific breakout room to show the Join/Leave button state
  const isInThisBreakoutRoom = currentClassUser?.breakout_group_id === breakoutGroupId;

  const onJoinBreakoutGroup = () => {
    if (!currentClassUser) return;
    storeApi.class_users
      .partial_update({
        id: currentClassUser.id,
        breakout_group_id: breakoutGroupId,
      })
      .catch(toastLocalizedOperationCatcher("failed_to_join_breakout_group"));
  };

  const onLeaveBreakoutGroup = () => {
    if (!currentClassUser) return;
    storeApi.class_users
      .partial_update({
        id: currentClassUser.id,
        breakout_group_id: null,
      })
      .catch(toastLocalizedOperationCatcher("failed_to_move_to_main_call"));
  };

  return (
    <div className="flex items-center gap-2">
      <PersonCount count={count} />
      {!isInABreakoutRoom && <BreakoutListeningToggleButton breakoutGroupId={breakoutGroupId} />}
      <Button
        className="border-none text-xs font-medium text-black-tint-20"
        eventName={`Breakouts - Settings / ${isInThisBreakoutRoom ? "Leave room" : "Join room"}`}
        kind="white"
        size="xs"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (isInThisBreakoutRoom) {
            onLeaveBreakoutGroup();
          } else {
            onJoinBreakoutGroup();
          }
        }}
      >
        {isInThisBreakoutRoom
          ? t("breakouts_control_panel.leave")
          : t("breakouts_control_panel.join")}
      </Button>
    </div>
  );
};

interface MoveToRoomSubMenuProps {
  classUserId: string;
  currentBreakoutGroupId: string;
}
const MoveToRoomSubMenu: FC<MoveToRoomSubMenuProps> = ({ classUserId, currentBreakoutGroupId }) => {
  const allBreakoutGroups = useListSelector(selectOrderedBreakoutGroups);
  const breakoutGroups = useMemo(
    () => allBreakoutGroups.filter((breakoutGroup) => breakoutGroup.id !== currentBreakoutGroupId),
    [allBreakoutGroups, currentBreakoutGroupId],
  );

  const onMoveRoom = (breakoutGroupId: string) => () => {
    serverUpdateClassUserBreakoutGroup(classUserId, breakoutGroupId).catch(
      toastLocalizedOperationCatcher("failed_to_move_to_room"),
    );
  };

  return (
    <SubMenu
      label={
        <IconText
          iconStart={<CopySendArrowIcon />}
          text={t("breakouts_control_panel.move_to_room")}
        />
      }
      overflow="auto"
      withArrow
    >
      {breakoutGroups.map((breakoutGroup) => {
        return (
          <MenuItem
            eventName="Breakouts - Settings / Move user to another room"
            key={breakoutGroup.id}
            onClick={onMoveRoom(breakoutGroup.id)}
          >
            <IconText
              iconStart={<PersonGroupIcon />}
              text={t("breakouts_control_panel.room_number", { number: breakoutGroup.number })}
            />
          </MenuItem>
        );
      })}
    </SubMenu>
  );
};

interface BreakoutsControlMenuProps {
  classUserId: string;
  breakoutGroupId: string;
}
export const BreakoutsControlMenu: FC<BreakoutsControlMenuProps> = ({
  classUserId,
  breakoutGroupId,
}) => {
  const onMoveToMainCall = () => {
    serverUpdateClassUserBreakoutGroup(classUserId, null).catch(
      toastLocalizedOperationCatcher("failed_to_move_to_main_call"),
    );
  };

  return (
    <SidebarOptionsMenu>
      <MoveToRoomSubMenu classUserId={classUserId} currentBreakoutGroupId={breakoutGroupId} />

      <MenuItem
        disabled={!breakoutGroupId}
        eventName="Breakouts - Settings / Move user to main call"
        onClick={onMoveToMainCall}
      >
        <IconText
          iconStart={<CopySendArrowIcon />}
          text={t("breakouts_control_panel.move_to_main_call")}
        />
      </MenuItem>
    </SidebarOptionsMenu>
  );
};

interface BreakoutGroupMemberProps {
  classUserId: string;
  breakoutGroupId: string;
  classUserIndex: number;
  onDropClassUser: (droppedItem: DraggableClassUserType) => void;
  onMoveClassUserLocally: (
    item: DraggableClassUserType,
    toIndex: number,
    dropContainerId: string,
  ) => void;
  isStarUser: boolean;
}
const BreakoutGroupMember = ({
  classUserId,
  breakoutGroupId,
  classUserIndex,
  onDropClassUser,
  onMoveClassUserLocally,
  isStarUser,
}: BreakoutGroupMemberProps) => {
  const user = useObjectSelector(
    (state) => selectUserByClassUserId(state, classUserId),
    PROFILE_PROPERTIES,
  );
  const isSpeaking = useAppSelector((state) => selectIsVirtualUserSpeaking(state, classUserId));
  if (!user) return null;
  return (
    <DragAndDrop
      dragItemKind="class_user"
      dropContainerId={breakoutGroupId}
      dropIndex={classUserIndex}
      item={
        {
          id: classUserId,
          breakout_group_id: breakoutGroupId,
          order: classUserIndex,
        } as DraggableClassUserType
      }
      key={classUserId}
      draggable
      droppable
      onDraggedItemHover={onMoveClassUserLocally}
      onDropItem={onDropClassUser}
    >
      <ListItem
        className="flex items-center justify-between"
        key={classUserId}
        grabbable
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <div className="flex items-center gap-2 text-sm font-medium">
          {isStarUser ? <StarIcon size="xs" /> : <PersonSingleIcon size="xs" />}
          {getUserFullName(user)}
          {isSpeaking && <SoundLevelAnimatedIcon className="text-blue" size="m" />}
        </div>
        <BreakoutsControlMenu breakoutGroupId={breakoutGroupId} classUserId={classUserId} />
      </ListItem>
    </DragAndDrop>
  );
};

type DraggableClassUserType = Pick<ClassUserType, "id" | "breakout_group_id"> & {
  order: number;
};

const TimeLeftCard = () => {
  const classSession = useAppSelector(selectActiveClassSession);
  const endTime = classSession?.breakout_session_ends_at;
  const endDate = endTime ? new Date(endTime) : null;
  const { timeRemainingStr, timeLabel } = useTimeRemaining(endDate);

  return (
    <div className="rounded-lg border border-blue-tint-70 bg-blue-tint-95 p-2">
      <div className="flex items-center justify-between text-sm font-medium">
        <div className="ml-1 flex items-center gap-2">
          <ClockIcon />
          {t("breakouts_control_panel.time_left")}
        </div>
        <div className="font-semibold text-blue">
          {timeRemainingStr} {timeLabel}
        </div>
      </div>
    </div>
  );
};

interface AddMoreTimeConfigFormValues {
  breakout_duration: number;
}

const AddMoreTimeConfig = () => {
  const classSessionId = useAppSelector(selectActiveClassSessionId);

  const form = useForm<AddMoreTimeConfigFormValues>({
    defaultValues: {
      breakout_duration: DEFAULT_BREAKOUT_ADD_MORE_TIME_DURATION,
    },
  });

  const breakoutDuration = form.watch("breakout_duration");

  const hasError = !!form.formState.errors.breakout_duration;

  const onSubmit = (values: AddMoreTimeConfigFormValues) => {
    if (!classSessionId) return;
    serverExtendBreakoutTime(classSessionId, values.breakout_duration).catch(
      toastLocalizedOperationCatcher("failed_to_extend_breakout_time"),
    );
  };

  return (
    <FormProvider {...form}>
      <form className="flex flex-col" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-black-tint-20">
            {t("breakouts_control_panel.add_more_time")}
          </div>
          <div className="flex items-center gap-2">
            <NumberFieldWithStepperButtons
              label={t("breakout_group_modal.min")}
              max={MAX_BREAKOUT_DURATION}
              min={MIN_BREAKOUT_DURATION}
              {...form.register("breakout_duration", {
                required: t("error.field_required"),
                max: {
                  value: MAX_BREAKOUT_DURATION,
                  message: t("error.duration_out_of_range", {
                    min: MIN_BREAKOUT_DURATION,
                    max: MAX_BREAKOUT_DURATION,
                  }),
                },
                min: {
                  value: MIN_BREAKOUT_DURATION,
                  message: t("error.duration_out_of_range", {
                    min: MIN_BREAKOUT_DURATION,
                    max: MAX_BREAKOUT_DURATION,
                  }),
                },
                validate: {
                  isWholeNumber: (value) => {
                    const number = Number(value);
                    if (!Number.isInteger(number)) {
                      return t("error.whole_number_required");
                    }
                    return true;
                  },
                },
              })}
            />

            <Tooltip text={form.formState.errors.breakout_duration?.message}>
              <Button
                disabled={hasError}
                eventName="Breakouts - Settings / Add time"
                eventProps={{ addedMinutes: breakoutDuration }}
                kind="blue"
                size="s"
                type="submit"
              >
                {t("edit_delete_widget.add")}
              </Button>
            </Tooltip>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const MANUALLY_END_BREAKOUTS_DELAY_MS = 5000;
const IMMEDIATELY_END_BREAKOUTS_THRESHOLD_MS = 9000;

const EndBreakoutsButton = () => {
  const classSession = useAppSelector(selectActiveClassSession);
  const endTime = classSession?.breakout_session_ends_at;
  const endDate = endTime ? new Date(endTime) : null;
  const { timeRemainingMs, timeRemainingStr } = useTimeRemaining(endDate);
  const [isWaitingForBreakoutsToEnd, setIsWaitingForBreakoutsToEnd] = useState(false);

  // If there are more than 9s remaining, set breakout_session_ends_at to 5 seconds from now,
  // then let useBreakoutEndObserver handle the rest. Otherwise, immediately end the breakout session.
  const onManuallyEndBreakouts = async () => {
    if (!classSession) return;

    const currentServerTimeMs = getCurrentServerTimeMs();
    if (!currentServerTimeMs) return;

    const shouldDelayEnd =
      timeRemainingMs !== null && timeRemainingMs > IMMEDIATELY_END_BREAKOUTS_THRESHOLD_MS;

    if (shouldDelayEnd) {
      try {
        await storeApi.class_sessions.partial_update({
          id: classSession.id,
          breakout_session_ends_at: new Date(
            currentServerTimeMs + MANUALLY_END_BREAKOUTS_DELAY_MS,
          ).toISOString(),
        });
        setIsWaitingForBreakoutsToEnd(true);
      } catch (error) {
        toastLocalizedOperationCatcher("failed_to_end_breakouts")(error);
      }
    } else {
      serverEndBreakouts(classSession.id);
    }
  };

  const buttonText = isWaitingForBreakoutsToEnd
    ? t("breakouts_control_panel.ending_breakouts_in_seconds", { seconds: timeRemainingStr })
    : t("breakouts_control_panel.end_breakouts_for_all");

  return (
    <Button
      className="text-black-tint-10"
      disabled={isWaitingForBreakoutsToEnd}
      eventName="Breakouts - Settings / End"
      kind="white"
      size="s"
      onClick={onManuallyEndBreakouts}
    >
      {buttonText}
    </Button>
  );
};

export const BreakoutsControlPanel: FC = () => {
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const { canAuthorSection } = useCapabilities();
  const classSession = useAppSelector(selectActiveClassSession);
  const isInBreakout = useAppSelector(selectIsInBreakout);
  const isInBreakoutRoom = useAppSelector(selectCurrentClassUserBreakoutGroupId);
  const currentClassUserId = useAppSelector(selectCurrentClassUserId);
  const breakoutGroups = useListSelector(selectFullBreakoutGroups);

  const starUserIds = useUserIdsWhoCanAuthorSection();
  const classUsersInMainCall = useListSelector(selectClassUsersInMainCall);

  const syncedPage = useAppSelector((s) =>
    pageSelectors.selectById(s, classSession?.breakout_session_synced_page_id),
  );

  const { togglePanel } = useOverlayControls(SecondaryOverlay.BreakoutSettings);

  const { isVirtualClassActive, isInVirtualClass } = useAv();

  const onAddBreakoutRoom = () => {
    if (!currentSectionId) return;
    storeApi.breakout_groups
      .create({ section_id: currentSectionId })
      .catch(toastLocalizedOperationCatcher("failed_to_add_breakout_room"));
  };

  useEffect(() => {
    if (!currentSectionId) return;
    storeApi.breakout_groups.list({ section_id: currentSectionId });
    if (!classSession?.breakout_session_synced_page_id) return;
    storeApi.page_groups.list({ page_id: classSession.breakout_session_synced_page_id });
  }, [currentSectionId, classSession?.breakout_session_synced_page_id]);

  // onMoveClassUserLocally handles drag events in both BreakoutsControlPanel and BreakoutGroupMember.
  // All it does is update the breakout_group_id of the dragged ClassUser item and set it equal to the
  // dropContainerId of whatever drop zone it's hovering over. This way when onDropClassUser is called,
  // the ClassUser will have the new value for breakout_group_id already set. The value of dropContainerId
  // will be a BreakoutGroup id when hovering over a BreakoutGroup element, or null when hovering over
  // the "main call" element.
  const onMoveClassUserLocally = (
    item: DraggableClassUserType,
    _toIndex: number,
    dropContainerId: string,
  ) => {
    // eslint-disable-next-line no-param-reassign
    item.breakout_group_id = dropContainerId;
  };

  // onDropClassUser handles drop events in both BreakoutsControlPanel and BreakoutGroupMember. It assumes
  // the current value of breakout_group_id set on the dragged item was set there by onMoveClassUserLocally,
  // and it performs the appropriate API request.
  const onDropClassUser = (droppedItem: DraggableClassUserType) => {
    serverUpdateClassUserBreakoutGroup(droppedItem.id, droppedItem.breakout_group_id).catch(
      toastLocalizedOperationCatcher("failed_to_move_to_room"),
    );
  };

  const onJoinMainRoom = () => {
    if (!currentClassUserId) return;
    storeApi.class_users
      .partial_update({
        id: currentClassUserId,
        breakout_group_id: null,
      })
      .catch(toastLocalizedOperationCatcher("failed_to_move_to_main_call"));
  };

  if (!isInBreakout || !canAuthorSection || !isVirtualClassActive || !isInVirtualClass) return null;

  return (
    <div className="flex min-h-full w-full flex-col gap-4 rounded-[0.625rem] border border-black-tint-90 bg-white px-5 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-hibiscus-tint-95">
            <PersonGroupIcon className="text-hibiscus" size="xs" />
          </div>
          <h3>{t("breakouts_control_panel.title")}</h3>
        </div>

        <CloseButton eventName="Right Panel - Close" size="s" onClick={togglePanel} />
      </div>

      <div className="flex flex-col gap-2">
        {syncedPage ? (
          <div className="flex items-center gap-1 truncate text-xs font-medium">
            <Tooltip text={t("group_configuration_card.breakouts_synced_with_this_page_tooltip")}>
              <CircleInfoIcon size="s" />
            </Tooltip>
            <Trans
              i18nKey="breakouts_control_panel.breakouts_synced_with_page"
              t={t}
              values={{ title: syncedPage.title }}
            >
              <Link
                className="truncate text-blue underline"
                to={pageUrl(syncedPage.section_id, syncedPage.id)}
              >
                {syncedPage.title}
              </Link>
            </Trans>
          </div>
        ) : null}

        <DragAndDrop
          dragItemKind="class_user"
          dropContainerId={null}
          droppable
          onDraggedItemHover={onMoveClassUserLocally}
          onDropItem={onDropClassUser}
        >
          <AccordionList
            color="red"
            emptyContent={
              <div className="mb-1 ml-1 text-sm">{t("breakouts_control_panel.empty_room")}</div>
            }
            title={t("breakouts_control_panel.main_call")}
            titleClassName="text-red-shade-20"
            titleRightContent={
              <div className="flex items-center gap-2">
                <PersonCount count={classUsersInMainCall.length} />
                {isInBreakoutRoom && (
                  <Button
                    className="border-none text-xs font-medium text-black-tint-20"
                    eventName={`Breakouts - Settings / "Join main room"}`}
                    kind="white"
                    size="xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onJoinMainRoom();
                    }}
                  >
                    {t("breakouts_control_panel.join")}
                  </Button>
                )}
              </div>
            }
            expandableAnywhere
            startOpen
          >
            {classUsersInMainCall.map((classUser, index) => (
              <BreakoutGroupMember
                breakoutGroupId={classUser.breakout_group_id}
                classUserId={classUser.id}
                classUserIndex={index}
                isStarUser={starUserIds.includes(classUser.user_id)}
                key={classUser.id}
                onDropClassUser={onDropClassUser}
                onMoveClassUserLocally={onMoveClassUserLocally}
              />
            ))}
          </AccordionList>
        </DragAndDrop>

        {breakoutGroups.map((group) => (
          <DragAndDrop
            dragItemKind="class_user"
            dropContainerId={group.id}
            key={group.id}
            droppable
            onDraggedItemHover={onMoveClassUserLocally}
            onDropItem={onDropClassUser}
          >
            <AccordionList
              color="blue"
              emptyContent={
                <div className="mb-1 ml-1 text-sm">{t("breakouts_control_panel.empty_room")}</div>
              }
              title={t("breakouts_control_panel.room_number", { number: group.number })}
              titleRightContent={
                <BreakoutRoomControls breakoutGroupId={group.id} count={group.members.length} />
              }
              expandableAnywhere
              startOpen
            >
              {group.members.map((member, classUserIndex) => (
                <BreakoutGroupMember
                  breakoutGroupId={group.id}
                  classUserId={member.id}
                  classUserIndex={classUserIndex}
                  isStarUser={starUserIds.includes(member.user_id)}
                  key={member.id}
                  onDropClassUser={onDropClassUser}
                  onMoveClassUserLocally={onMoveClassUserLocally}
                />
              ))}
            </AccordionList>
          </DragAndDrop>
        ))}

        <LinkButton
          className="ml-3 py-2 text-sm font-medium text-blue"
          eventName="Breakouts - Settings / Add new room"
          onClick={onAddBreakoutRoom}
        >
          {t("breakouts_control_panel.add_new_room")}
        </LinkButton>
      </div>

      <TimeLeftCard />

      <AddMoreTimeConfig />

      <EndBreakoutsButton />
    </div>
  );
};
